import { z } from 'zod';

import { withoutHiddenStages } from '../constants/hidden-stages';
import { STAGE_FLOW_ORDER } from '../utils/lineage-images-search';

import type { Stage } from './lineage-images.api.types';
import type {
  ArchiveFilter,
  CoverageBucket,
  FlowType,
  RunListStatus,
  RunSortField,
  RunSortOrder,
  RunStatusCounts,
  RunTrigger,
  UseCase,
} from './runs.api.types';

/** Run-list status tabs in display order; `ALL` sends no `status` param. */
export const RUN_LIST_TABS = [
  'ALL',
  'PENDING',
  'RUNNING',
  'AWAITING_GATE',
  'COMPLETED',
  'DELIVERING',
  'FAILED',
  'REJECTED',
  'RESET',
  'TERMINATED',
  'SKIPPED',
] as const satisfies readonly ('ALL' | RunListStatus)[];

/** One run-list tab value. */
export type RunListTab = (typeof RUN_LIST_TABS)[number];

/**
 * The 5 tabs shown as pills (P5): All, Needs you (AWAITING_GATE), Running, Completed,
 * Failed. `status` only ever accepts ONE value server-side, so "Running" maps to the
 * single closest status rather than merging PENDING/RUNNING/DELIVERING.
 */
export const RUN_LIST_PRIMARY_TABS = [
  'ALL',
  'AWAITING_GATE',
  'RUNNING',
  'COMPLETED',
  'FAILED',
] as const satisfies readonly RunListTab[];

/** Every other tab, tucked behind "More ▾" so the bar stays to 5 primary pills. */
export const RUN_LIST_MORE_TABS = [
  'PENDING',
  'DELIVERING',
  'REJECTED',
  'RESET',
  'TERMINATED',
  'SKIPPED',
] as const satisfies readonly RunListTab[];

/** Which `RunStatusCounts` field badges each tab — the tabs partition the list. */
export const RUN_LIST_TAB_COUNT_FIELD: Record<RunListTab, keyof RunStatusCounts> = {
  ALL: 'all',
  PENDING: 'pending',
  RUNNING: 'running',
  AWAITING_GATE: 'awaitingGate',
  COMPLETED: 'completed',
  DELIVERING: 'delivering',
  FAILED: 'failed',
  REJECTED: 'rejected',
  RESET: 'reset',
  TERMINATED: 'terminated',
  SKIPPED: 'skipped',
};

/** `triggerSource` filter values (contract §0.8 `RunTrigger`). */
export const RUN_TRIGGERS = [
  'API',
  'SCHEDULE',
  'EXPERIMENT',
  'RETRY',
] as const satisfies readonly RunTrigger[];

/** `useCase` filter values (contract §0.8 / generated `UseCase`). */
export const RUN_USE_CASES = [
  'GMC_ADS',
  'META_ADS',
  'ITEM_SETUP',
  'ONSITE_SEARCH',
] as const satisfies readonly UseCase[];

/** Archived-visibility values; ACTIVE is the server default. */
export const RUN_ARCHIVE_FILTERS = [
  'ACTIVE',
  'ARCHIVED',
  'ALL',
] as const satisfies readonly ArchiveFilter[];

/** `coverage` filter values (contract §0.8 `CoverageBucket`) — over the newest ENRICHMENT attempt. */
export const RUN_COVERAGE_BUCKETS = [
  'BELOW_90',
  'BETWEEN_90_95',
  'BETWEEN_95_100',
] as const satisfies readonly CoverageBucket[];

/** `sortBy` values GET catalog-runs accepts (anything else is a 422). */
export const RUN_SORT_FIELDS = [
  'createdAt',
  'startedAt',
  'completedAt',
  'duration',
  'productsSucceeded',
  'productsFailed',
] as const satisfies readonly RunSortField[];

/** `sortOrder` values; the server defaults to DESC. */
export const RUN_SORT_ORDERS = ['ASC', 'DESC'] as const satisfies readonly RunSortOrder[];

/** The server's own default sort — left out of the URL and the wire so links stay short. */
export const DEFAULT_RUN_SORT_FIELD: RunSortField = 'createdAt';
export const DEFAULT_RUN_SORT_ORDER: RunSortOrder = 'DESC';

/** The run list's effective sort (URL value or the server default). */
export interface RunSort {
  field: RunSortField;
  order: RunSortOrder;
}

/** Stages the Stage filter offers, in flow order — hidden ones (Image check) excluded: picking
 *  Feed intake also matches runs stopped at Image check (see `toRunsFilterParams`). */
/** Engine steps a batch page never shows as a step of its own — not offered as a filter. */
const INTERNAL_FILTER_STAGES: ReadonlySet<string> = new Set([
  'FETCH',
  'SPLIT',
  'SAMPLE_SELECT',
  'ROLLUPS',
  'EXPORT',
]);

export const RUN_FILTER_STAGES: readonly Stage[] = withoutHiddenStages(STAGE_FLOW_ORDER).filter(
  (stage) => !INTERNAL_FILTER_STAGES.has(stage),
);

/** Default and allowed page sizes for the run table (server caps `size` at 100). */
export const RUNS_DEFAULT_PAGE_SIZE = 25;
export const RUNS_PAGE_SIZE_OPTIONS = [25, 50, 100];

/** Which band of the Batches page is showing (S12): the paged table, or the failure-patterns
 *  view grouped by stage + reason. Lives in the URL (`?view=failures`) so it survives Back. */
export const RUNS_VIEWS = ['list', 'failures'] as const;
export type RunsView = (typeof RUNS_VIEWS)[number];

/** How far back the failure-patterns view looks; the server defaults to 7 days with no `since`. */
export const FAILURE_WINDOW_DAYS = [1, 7, 30] as const;
export type FailureWindowDays = (typeof FAILURE_WINDOW_DAYS)[number];
export const DEFAULT_FAILURE_WINDOW_DAYS: FailureWindowDays = 7;

const isoDay = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Run-list filters kept in the URL (`/catalog-enrichment/runs?…`) so they survive Back and
 * can be shared. Every key is optional and a malformed value falls back to "unset"
 * rather than throwing, so a hand-edited URL still opens the page.
 */
export const runsListSearchSchema = z.object({
  status: z.enum(RUN_LIST_TABS).optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  connectorCode: z.string().optional().catch(undefined),
  flow: z.string().optional().catch(undefined),
  triggerSource: z.enum(RUN_TRIGGERS).optional().catch(undefined),
  useCase: z.enum(RUN_USE_CASES).optional().catch(undefined),
  /** Local calendar day `YYYY-MM-DD`, inclusive. */
  createdFrom: z.string().regex(isoDay).optional().catch(undefined),
  /** Local calendar day `YYYY-MM-DD`, inclusive (sent as the next day's start). */
  createdTo: z.string().regex(isoDay).optional().catch(undefined),
  archived: z.enum(RUN_ARCHIVE_FILTERS).optional().catch(undefined),
  coverage: z.enum(RUN_COVERAGE_BUCKETS).optional().catch(undefined),
  /** Hides runs started from another run (restarts, trial stages) when true. */
  rootsOnly: z.boolean().optional().catch(undefined),
  /** Deep link from a batch's History section ("See all N re-runs") — the API's `?parentRunId=`. */
  parentRunId: z.string().uuid().optional().catch(undefined),
  /** Actor ids whose runs to show (`audit.createdBy.actorId`), repeated on the wire. */
  createdBy: z.array(z.string().uuid()).optional().catch(undefined),
  /** Current stage — for a finished run, the stage it stopped at — repeated on the wire. */
  currentStage: z.array(z.enum(STAGE_FLOW_ORDER)).optional().catch(undefined),
  /** Sort column; unset is the server default (`createdAt`). */
  sortBy: z.enum(RUN_SORT_FIELDS).optional().catch(undefined),
  /** Sort direction; unset is the server default (`DESC`). */
  sortOrder: z.enum(RUN_SORT_ORDERS).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  size: z.number().int().min(1).max(100).optional().catch(undefined),
  /** S12: the Batches page's active band — unset is the same as `'list'`. */
  view: z.enum(RUNS_VIEWS).optional().catch(undefined),
  /** Failure-patterns view: how many days back to read (server call, unlike the two below). */
  failuresWindow: z
    .union([
      z.literal(FAILURE_WINDOW_DAYS[0]),
      z.literal(FAILURE_WINDOW_DAYS[1]),
      z.literal(FAILURE_WINDOW_DAYS[2]),
    ])
    .optional()
    .catch(undefined),
  /** Failure-patterns view: narrows to one stage — filtered client-side over the loaded groups. */
  failuresStage: z.string().optional().catch(undefined),
  /** Failure-patterns view: hides patterns whose newest batch has since succeeded — client-side. */
  failuresOnlyActive: z.boolean().optional().catch(undefined),
  /** The batch open in the detail side panel, so a panel link can be shared. */
  batch: z.string().uuid().optional().catch(undefined),
});

/** Parsed run-list URL search params. */
export type RunsListSearch = z.infer<typeof runsListSearchSchema>;

/** Filters shared by the list, its tab counts and (partly) the KPI strip — wire spelling. */
export interface RunsFilterParams {
  search?: string;
  connectorCode?: string;
  flow?: string;
  triggerSource?: RunTrigger;
  useCase?: UseCase;
  /** ISO date-time, inclusive. */
  createdFrom?: string;
  /** ISO date-time, exclusive. */
  createdTo?: string;
  archived?: ArchiveFilter;
  coverage?: CoverageBucket;
  rootsOnly?: boolean;
  /** Narrows to the runs started FROM this one — the History section's "See all re-runs" link. */
  parentRunId?: string;
  /** Narrows to one experiment's batches — the experiment page's Batches tab. */
  experimentId?: string;
  /** Actor ids (`audit.createdBy.actorId`); repeated query key. */
  createdBy?: string[];
  /** Current / stopped-at stages; repeated query key. */
  currentStage?: Stage[];
}

/** GET catalog-runs query: the shared filters plus tab, page and sort. A page cursor is bound to
 *  its sort server-side, so any sort change must restart paging (the page resets it). */
export interface RunsListParams extends RunsFilterParams {
  status?: RunListStatus;
  page: number;
  size: number;
  sortBy?: RunSortField;
  sortOrder?: RunSortOrder;
}

/** Narrowing accepted by the KPI strip and the failures card (a subset of the list filters). */
export interface RunsAggregateParams {
  connectorCode?: string;
  flow?: string;
  triggerSource?: RunTrigger;
  /** Pipeline family; the failures read defaults to CATALOG_OPERATIONS when omitted (backend ask). */
  flowType?: FlowType;
}

/** GET catalog-runs/failures query (S12 adds `since`/`until` — the failure-patterns view's own
 *  time-window selector; the summary callout always calls this with neither, for the server's
 *  default 7-day window). */
export interface RunFailuresParams extends Pick<
  RunsAggregateParams,
  'connectorCode' | 'flow' | 'flowType'
> {
  /** Window start, inclusive — ISO date-time. */
  since?: string;
  /** Window end, exclusive — ISO date-time. */
  until?: string;
}
